/**
 * GestorRemesasDashboardPage — Centro Financiero de Remesas (gestor).
 *
 * Independent from the legacy "Mi Wallet" (which tracks PASAJES).
 * Shows:
 *   • Dual-currency balance cards (UYU + USD)  — operational currencies only.
 *     USDT is an INTERNAL CEO asset and never appears here.
 *   • Usage gauge per currency with 80% warning + 100% block badges
 *   • Inline deposit declaration form (UYU / USD)
 *   • Recent deposits (pending / confirmed / rejected)
 *   • Recent fund movements (append-only ledger)
 *
 * Subscribes to WS `remittance.fund.changed` so the panel refreshes the
 * instant the CEO confirms a deposit or after the gestor creates a remesa.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, ArrowDownCircle, ArrowUpCircle, History, Send, ShieldCheck,
  Wallet, DollarSign, Loader2,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useRealtime } from '@/contexts/RealtimeContext';
import { extractErrorMessage } from '@/utils/errors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CCY_META = {
  UYU: { label: 'Pesos uruguayos', Icon: Wallet,      accent: 'bg-amber-50',    fg: 'text-amber-700' },
  USD: { label: 'Dólares (USD)',   Icon: DollarSign,  accent: 'bg-emerald-50', fg: 'text-emerald-700' },
};

const StatusBadge = ({ status }) => {
  const map = {
    pending:   { label: 'Pendiente',  klass: 'bg-amber-100 text-amber-700' },
    confirmed: { label: 'Confirmado', klass: 'bg-emerald-100 text-emerald-700' },
    rejected:  { label: 'Rechazado',  klass: 'bg-rose-100 text-rose-700' },
  };
  const m = map[status] || { label: status, klass: 'bg-slate-100 text-slate-600' };
  return <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${m.klass}`}>{m.label}</span>;
};

const FundCard = ({ ccy, bucket, alertPct }) => {
  const meta = CCY_META[ccy];
  const usage = bucket.usage_pct || 0;
  const barColor = bucket.block ? 'bg-rose-500' : bucket.alert ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <Card className={`border-0 shadow-sm overflow-hidden ${meta.accent}`} data-testid={`fund-card-${ccy}`}>
      <div className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <meta.Icon className={`w-5 h-5 ${meta.fg}`} />
            <p className="text-sm font-semibold text-slate-700">{meta.label}</p>
          </div>
          {bucket.block && (
            <Badge variant="destructive" className="text-[10px]" data-testid={`fund-block-${ccy}`}>FONDO AGOTADO</Badge>
          )}
          {bucket.alert && !bucket.block && (
            <Badge className="bg-amber-500 text-white text-[10px] flex items-center gap-1" data-testid={`fund-alert-${ccy}`}>
              <AlertTriangle className="w-3 h-3" /> Sobre {alertPct}%
            </Badge>
          )}
        </div>
        <p className={`text-3xl font-bold ${meta.fg} font-mono`} data-testid={`fund-balance-${ccy}`}>
          {bucket.balance.toLocaleString('es-ES', { maximumFractionDigits: 2 })} <span className="text-base font-normal">{ccy}</span>
        </p>
        <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-600">
          <div>Depositado: <b className="text-slate-900">{bucket.deposited.toFixed(2)}</b></div>
          <div>Enviado: <b className="text-slate-900">{bucket.sent.toFixed(2)}</b></div>
          <div>Ajustes: <b className="text-slate-900">{bucket.adjustments.toFixed(2)}</b></div>
          <div>Movs: <b className="text-slate-900">{bucket.movements}</b></div>
        </div>
        <div>
          <div className="flex items-center justify-between text-[10px] uppercase tracking-wide text-slate-500 mb-1">
            <span>Uso</span>
            <span data-testid={`fund-usage-${ccy}`}>{usage.toFixed(1)}%</span>
          </div>
          <div className="w-full h-2 bg-slate-200 rounded overflow-hidden">
            <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.min(100, usage)}%` }} />
          </div>
        </div>
      </div>
    </Card>
  );
};

const DepositForm = ({ onCreated }) => {
  const { getAuthHeaders } = useAuth();
  const [form, setForm] = useState({ currency: 'UYU', amount: '', method: 'bank_transfer', notes: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(form.amount);
    if (!amt || amt <= 0) {
      toast.error('Monto inválido');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        currency: form.currency,
        amount: amt,
        method: form.method,
        notes: form.notes || null,
      };
      await axios.post(`${API}/gestor/me/remittance-deposits`, payload, { headers: getAuthHeaders() });
      toast.success('Depósito declarado. Espera confirmación del CEO.');
      setForm({ currency: 'UYU', amount: '', method: 'bank_transfer', notes: '' });
      onCreated?.();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo declarar el depósito'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-0 shadow-sm p-5" data-testid="deposit-form-card">
      <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
        <ArrowDownCircle className="w-4 h-4 text-emerald-600" /> Declarar depósito
      </p>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-medium">Moneda</label>
            <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
              <SelectTrigger data-testid="deposit-currency-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="UYU">UYU (peso uruguayo)</SelectItem>
                <SelectItem value="USD">USD (dólares)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-medium">Monto</label>
            <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required data-testid="deposit-amount-input" />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase text-slate-500 font-medium">Método</label>
          <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
            <SelectTrigger data-testid="deposit-method-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="bank_transfer">Transferencia bancaria</SelectItem>
              <SelectItem value="cash">Efectivo</SelectItem>
              <SelectItem value="other">Otro</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] uppercase text-slate-500 font-medium">Notas (opcional)</label>
          <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Referencia, comprobante, etc." data-testid="deposit-notes" />
        </div>
        <Button type="submit" disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="deposit-submit-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowDownCircle className="w-4 h-4 mr-2" />}
          Declarar depósito
        </Button>
      </form>
    </Card>
  );
};

const GestorRemesasDashboardPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const realtime = useRealtime();
  const [fund, setFund] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, d, m] = await Promise.all([
        axios.get(`${API}/gestor/me/remittance-fund`,            { headers: getAuthHeaders() }),
        axios.get(`${API}/gestor/me/remittance-deposits`,        { headers: getAuthHeaders() }),
        axios.get(`${API}/gestor/me/remittance-fund/movements`,  { headers: getAuthHeaders() }),
      ]);
      setFund(f.data);
      setDeposits(d.data.items || []);
      setMovements(m.data.items || []);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[GestorDashboard] load failed', err);
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!realtime?.subscribe) return undefined;
    const offFund = realtime.subscribe('remittance.fund.changed', (data) => {
      if (data?.gestor_id !== user?.id) return;
      load();
    });
    const offNotif = realtime.subscribe('notification.new', () => load());
    return () => { offFund?.(); offNotif?.(); };
  }, [realtime, user, load]);

  const alertPct = fund?.alert_pct || 80;
  const emptyBucket = { balance: 0, deposited: 0, sent: 0, adjustments: 0, movements: 0, usage_pct: 0, alert: false, block: false };
  const kpis = fund?.kpis || { UYU: emptyBucket, USD: emptyBucket };
  const uyuBucket = kpis.UYU || emptyBucket;
  const usdBucket = kpis.USD || emptyBucket;
  const anyAlert = uyuBucket.alert || usdBucket.alert || uyuBucket.block || usdBucket.block;

  const totalBalanceUYU = uyuBucket.balance;
  const totalBalanceUSD = usdBucket.balance;

  const top = useMemo(() => deposits.slice(0, 5), [deposits]);
  const movTop = useMemo(() => movements.slice(0, 10), [movements]);

  return (
    <div className="space-y-6" data-testid="gestor-dashboard-financiero">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Centro financiero de remesas</h1>
          <p className="text-xs text-slate-500 mt-1">
            Saldos dedicados a remesas — independientes de la wallet de pasajes. KPIs privados, sin ranking.
          </p>
        </div>
        <Link to="/remesas/enviar">
          <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="dashboard-cta-enviar">
            <Send className="w-4 h-4 mr-2" /> Enviar remesa
          </Button>
        </Link>
      </div>

      {/* Master alert banner */}
      {anyAlert && (
        <div className="border-l-4 border-amber-500 bg-amber-50 p-3 rounded flex items-start gap-3" data-testid="fund-alert-banner">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-amber-900">
              {(uyuBucket.block || usdBucket.block) ? 'Fondo agotado' : `Fondo cerca del ${alertPct}%`}
            </p>
            <p className="text-xs text-amber-800 mt-1">
              {(uyuBucket.block || usdBucket.block)
                ? 'Has alcanzado el 100% de uso. Declara un nuevo depósito para continuar.'
                : `Has superado el ${alertPct}% del fondo operativo. El CEO ya fue notificado.`}
            </p>
          </div>
        </div>
      )}

      {/* Fund cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FundCard ccy="UYU" bucket={uyuBucket} alertPct={alertPct} />
        <FundCard ccy="USD" bucket={usdBucket} alertPct={alertPct} />
      </div>

      {/* Deposit declaration + recent deposits */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DepositForm onCreated={load} />

        <Card className="border-0 shadow-sm overflow-hidden" data-testid="deposits-history-card">
          <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
            <p className="text-sm font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-slate-500" /> Mis depósitos</p>
          </div>
          {loading ? (
            <div className="p-6 text-center text-slate-400 text-sm"><Loader2 className="w-4 h-4 inline animate-spin" /> Cargando…</div>
          ) : top.length === 0 ? (
            <p className="p-6 text-center text-slate-400 text-sm">Aún no declaras depósitos.</p>
          ) : (
            <table className="w-full text-xs">
              <thead><tr className="bg-slate-50 border-b text-left text-[10px] text-slate-500 uppercase"><th className="p-2">Moneda</th><th className="p-2 text-right">Monto</th><th className="p-2">Estado</th><th className="p-2">Fecha</th></tr></thead>
              <tbody>
                {top.map((d) => (
                  <tr key={d.id} className="border-b last:border-0">
                    <td className="p-2 font-semibold">{d.currency}</td>
                    <td className="p-2 text-right font-mono">{d.amount.toFixed(2)}</td>
                    <td className="p-2"><StatusBadge status={d.status} /></td>
                    <td className="p-2 text-slate-400">{new Date(d.created_at).toLocaleString('es-ES')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      {/* Movements (append-only ledger) */}
      <Card className="border-0 shadow-sm overflow-hidden" data-testid="fund-movements-card">
        <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
          <p className="text-sm font-semibold flex items-center gap-2"><History className="w-4 h-4 text-slate-500" /> Movimientos recientes</p>
          <span className="text-[10px] text-slate-400">Ledger append-only · auditado</span>
        </div>
        {loading ? (
          <div className="p-6 text-center text-slate-400 text-sm"><Loader2 className="w-4 h-4 inline animate-spin" /> Cargando…</div>
        ) : movTop.length === 0 ? (
          <p className="p-6 text-center text-slate-400 text-sm">Sin movimientos todavía.</p>
        ) : (
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-50 border-b text-left text-[10px] text-slate-500 uppercase"><th className="p-2">Tipo</th><th className="p-2">Moneda</th><th className="p-2 text-right">Monto</th><th className="p-2">Descripción</th><th className="p-2">Fecha</th></tr></thead>
            <tbody>
              {movTop.map((m) => {
                const isCredit = m.amount >= 0;
                return (
                  <tr key={m.id} className="border-b last:border-0">
                    <td className="p-2">
                      {isCredit
                        ? <span className="inline-flex items-center gap-1 text-emerald-700"><ArrowDownCircle className="w-3 h-3" />{m.kind}</span>
                        : <span className="inline-flex items-center gap-1 text-rose-700"><ArrowUpCircle className="w-3 h-3" />{m.kind}</span>}
                    </td>
                    <td className="p-2 font-semibold">{m.currency}</td>
                    <td className={`p-2 text-right font-mono ${isCredit ? 'text-emerald-700' : 'text-rose-700'}`}>{isCredit ? '+' : ''}{m.amount.toFixed(2)}</td>
                    <td className="p-2 text-slate-600">{m.description}</td>
                    <td className="p-2 text-slate-400">{new Date(m.created_at).toLocaleString('es-ES')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      <p className="text-[10px] text-slate-400 text-center">
        Balance UYU: <b>{totalBalanceUYU.toFixed(2)}</b> · Balance USD: <b>{totalBalanceUSD.toFixed(2)}</b> · Datos privados protegidos por RBAC.
      </p>
    </div>
  );
};

export default GestorRemesasDashboardPage;
